import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import ClientApiV2 from '../client-api-v2';
import type {
  GrantAlertHistoryItem,
  GrantAlertHistoryResponse,
  GrantAlertKind,
} from '../grant-alert-history-types';

type GrantAlertHistoryState = {
  items: GrantAlertHistoryItem[];
  total: number;
  status: 'idle' | 'pending' | 'failed';
  latestRequestId?: string;
};

const initialState: GrantAlertHistoryState = {
  items: [],
  total: 0,
  status: 'idle',
};

export const fetchGrantAlertHistory = createAsyncThunk<
  GrantAlertHistoryResponse,
  {
    api: ClientApiV2;
    locale: string;
    page: number;
    limit: number;
    types: GrantAlertKind[];
  }
>(
  'client/fetchGrantAlertHistory',
  async ({ api, locale, page, limit, types }) => {
    const response = await api.get<GrantAlertHistoryResponse>('/grant-alerts', {
      params: {
        locale,
        page,
        limit,
        types: types.length > 0 ? types.join(',') : undefined,
      },
    });
    return response.data;
  },
);

const grantAlertHistorySlice = createSlice({
  name: 'grantAlertHistory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchGrantAlertHistory.pending, (state, action) => {
      state.items = [];
      state.total = 0;
      state.status = 'pending';
      state.latestRequestId = action.meta.requestId;
    });
    builder.addCase(fetchGrantAlertHistory.fulfilled, (state, action) => {
      if (state.latestRequestId !== action.meta.requestId) {
        return;
      }

      state.items = action.payload.alerts;
      state.total = action.payload.total;
      state.status = 'idle';
      state.latestRequestId = undefined;
    });
    builder.addCase(fetchGrantAlertHistory.rejected, (state, action) => {
      if (state.latestRequestId !== action.meta.requestId) {
        return;
      }

      state.status = 'failed';
      state.latestRequestId = undefined;
    });
  },
});

export const selectGrantAlertHistoryItems = (state: RootState) =>
  state.grantAlertHistory.items;
export const selectGrantAlertHistoryTotal = (state: RootState) =>
  state.grantAlertHistory.total;
export const selectGrantAlertHistoryStatus = (state: RootState) =>
  state.grantAlertHistory.status;

export const grantAlertHistoryReducer = grantAlertHistorySlice.reducer;
